import type { JSX } from 'react'
import { Chips } from 'primereact/chips'

/**
 * RecipientsChipsInput
 * ---------------------
 * Thin wrapper around PrimeReact `Chips` that enforces one valid email per
 * chip. Returning `false` from `onAdd` cancels the add (see PrimeReact
 * `Chips.addItem` impl) — so any string that fails `EMAIL_REGEX` is rejected
 * before it ever lands in `props.value`.
 *
 * Spec: UI-6a Task 5. Used by ScheduledReportDrawer.
 */
export interface RecipientsChipsInputProps {
  value: string[]
  onChange: (next: string[]) => void
}

// Pragmatic email regex — local @ domain.tld. Mirrors RFC-lite checks used
// elsewhere in the codebase; full RFC 5322 is overkill for a recipients list.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function RecipientsChipsInput(props: RecipientsChipsInputProps): JSX.Element {
  return (
    <Chips
      value={props.value}
      onChange={(e) => props.onChange(e.value ?? [])}
      onAdd={(e) => {
        // Return false to abort the add. PrimeReact reads the return value of
        // onAdd; non-false means "allow", so we explicitly return `false` for
        // invalid emails.
        if (!EMAIL_REGEX.test(e.value)) {
          return false as unknown as void
        }
        return undefined
      }}
      placeholder="Enter email + press Enter"
      aria-label="Report recipients"
      separator=","
      allowDuplicate={false}
    />
  )
}
